import { useCallback, useMemo, useState } from 'react';
import { isVerticalOrientation } from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { useCropImageModal } from 'modules/campaigns/modules/creative-editor/components/MediaTabs/CroppingToolsForm/hooks/useCropImageModal';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import useFormFieldValue from 'modules/taboola-common-frontend-modules/formData/hooks/useFormFieldValue';
import { useFormValidatedValue } from 'modules/taboola-common-frontend-modules/validations';
import validationFunctions from 'modules/taboola-common-frontend-modules/validations/services/validationFunctions';
import { COMPONENT_STATUS } from 'services/constants';
import { FORM_MODES, thumbnailAutoReviewValidations, useInsignificantChangesValidation } from '../../../config';

const baseValidations = [
    {
        validationFn: validationFunctions.isNotEmpty,
        messageId: 'validations.error.creative.editor.thumbnail.empty',
        defaultMessage: 'Media file cannot be empty',
    },
];

export const useThumbnailEditorState = ({ campaignId, isThumbnailLoading, setIsThumbnailLoading }) => {
    const [error, setError] = useState(null);

    const isContentValidationEnabled = useConfigMatch({
        [FEATURE_FLAGS.AUTO_REVIEWER_CONTENT_VALIDATIONS_ENABLED]: 'true',
    });

    const validationsWithFF = useMemo(
        () => (isContentValidationEnabled ? [...baseValidations, ...thumbnailAutoReviewValidations] : baseValidations),
        [isContentValidationEnabled]
    );
    const urlValidations = useInsignificantChangesValidation({ baseValidations: validationsWithFF, isForUrl: true });
    const {
        data: { orientation },
        formAccount: { accountId },
        mode,
        setSubmitStatus,
    } = useFormDataContext();
    const isVerticalMedia = isVerticalOrientation({ orientation });
    const {
        value: thumbnailUrl,
        onChange: onChangeThumbnailUrl,
        failedValidationData,
        scrollRef,
    } = useFormValidatedValue({
        validations: urlValidations,
        field: 'thumbnailUrl',
        validationDependencies: {
            accountId,
        },
    });
    const setThumbnailData = useCallback(
        thumbnailUrl => {
            onChangeThumbnailUrl(thumbnailUrl);

            setError(null);
        },
        [onChangeThumbnailUrl]
    );
    const [lastSelectedId, setLastSelectedId] = useState();
    const selectedMedia = useMemo(() => (lastSelectedId ? [{ id: lastSelectedId }] : []), [lastSelectedId]);

    const { openModal: openCropModal } = useCropImageModal({
        campaignId,
        isVerticalMedia,
    });
    const { onChange: onChangeCropped } = useFormFieldValue({ field: 'creativeCrop' });
    const isCroppedModeEnable = mode === FORM_MODES.DUPLICATE || mode === FORM_MODES.EDIT;

    const onSelectMedia = useCallback(
        async (urlResolver, metadata) => {
            if (metadata.selected) {
                return;
            }
            setIsThumbnailLoading(true);
            setSubmitStatus(COMPONENT_STATUS.LOADING);

            try {
                const { url } = await urlResolver();
                setThumbnailData(url, { cdnUrl: url, ...metadata });
                setLastSelectedId(metadata?.id);
                onChangeCropped({ cropData: [] });
                if (isCroppedModeEnable) {
                    openCropModal({ src: url, metadata });
                }
            } catch (error) {
                setError(error);
            }

            setIsThumbnailLoading(false);
            setSubmitStatus(COMPONENT_STATUS.ACTIVE);
        },
        [setIsThumbnailLoading, setSubmitStatus, setThumbnailData, openCropModal, isCroppedModeEnable, onChangeCropped]
    );
    const isThumbnailSelected = thumbnailUrl || isThumbnailLoading || error;

    return {
        error,
        thumbnailUrl,
        failedValidationData,
        scrollRef,
        selectedMedia,
        openCropModal,
        onSelectMedia,
        isThumbnailSelected,
    };
};
